import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PORTAL_AUTH_DATA } from 'graphql/queries/portal';
import { MenuTreeNode, PortalAuthDataResponse } from 'graphql/types';
import { isLoggedIn, isMustChangePassword } from 'utils/auth';

interface PermissionContextValue {
    permissions: Set<string>;
    menuTree: MenuTreeNode[];
    loading: boolean;
    /** 权限数据是否已完成首次加载（区分"未加载"和"加载完毕无权限"） */
    initialized: boolean;
    hasPermission: (code: string) => boolean;
    /** 触发权限数据重新加载（登录后调用） */
    refresh: () => void;
}

const PermissionContext = createContext<PermissionContextValue>({
    permissions: new Set(),
    menuTree: [],
    loading: false,
    initialized: false,
    hasPermission: () => false,
    refresh: () => {},
});

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // 用 state 驱动重渲染，使 shouldFetch 在登录后重新求值
    const [, setTick] = useState(0);
    const refresh = useCallback(() => setTick((t) => t + 1), []);

    const shouldFetch = isLoggedIn() && !isMustChangePassword();

    const { data, loading } = useQuery<PortalAuthDataResponse>(GET_PORTAL_AUTH_DATA, {
        skip: !shouldFetch,
    });

    // 已完成首次加载：不在 loading 中，且 query 未被 skip（shouldFetch=true 时 data 不为 undefined 说明已返回）
    const initialized = shouldFetch ? !loading && data !== undefined : false;

    const value = useMemo<PermissionContextValue>(() => {
        const permissions = new Set(data?.portalPermissions || []);
        return {
            permissions,
            menuTree: data?.portalMenuTree || [],
            loading,
            initialized,
            hasPermission: (code: string) => permissions.has(code),
            refresh,
        };
    }, [data, loading, initialized, refresh]);

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermission = () => useContext(PermissionContext);
